import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getEventById, createPaymentSession, getBusSchedules, buyTicketByType } from '../services/api';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import useWindowSize from '../hooks/useWindowSize';
import FeeInfoButton from '../components/FeeInfoButton';

function Checkout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [attendees, setAttendees] = useState([{ name: '', email: '', dni: '', birthdate: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busIda, setBusIda] = useState('');
  const [busVuelta, setBusVuelta] = useState('');
  const [busSchedules, setBusSchedules] = useState([]);
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const ticketType = location.state?.ticketType || null;
  const initialQuantity = location.state?.quantity || 1;

  const calcFee = (price) => (price * 0.10).toFixed(2);

  useEffect(() => {
    getEventById(id).then(res => setEvent(res.data));
    if (initialQuantity) setQuantity(initialQuantity);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ticketType?.includes_bus === true || ticketType?.includes_bus === 'true') {
      getBusSchedules(id)
        .then(res => setBusSchedules(res.data))
        .catch(() => setBusSchedules([]));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setAttendees(a => Array.from({ length: quantity }, (_, i) => a[i] || { name: '', email: '', dni: '', birthdate: '' }));
  }, [quantity]);

  const handleAttendeeChange = (index, field, value) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], [field]: value };
    setAttendees(updated);
  };

  const handleBuy = async () => {
    setError('');

    if (ticketType?.includes_bus === true || ticketType?.includes_bus === 'true') {
      if (!busIda) return setError('Selecciona el horario de ida del autobús');
      if (!busVuelta) return setError('Selecciona el horario de vuelta del autobús');
    }

    for (let i = 0; i < attendees.length; i++) {
      if (!attendees[i].name.trim()) return setError(`Añade el nombre del asistente ${i + 1}`);
      if (!attendees[i].email.trim()) return setError(`Añade el email del asistente ${i + 1}`);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendees[i].email)) return setError(`El email del asistente ${i + 1} no es válido`);
      if (!attendees[i].dni.trim()) return setError(`Añade el DNI del asistente ${i + 1}`);
      if (!attendees[i].birthdate) return setError(`Añade la fecha de nacimiento del asistente ${i + 1}`);
    }

    setLoading(true);

    // Entradas gratuitas: se crean directamente, sin pasar por Stripe
    if (ticketType?.is_free) {
      try {
        const res = await buyTicketByType({
          event_id: id,
          ticket_type_id: ticketType.id,
          quantity,
          attendees,
        });
        navigate('/payment-success', { state: { freeOrderData: res.data } });
      } catch (err) {
        setError(err.response?.data?.error || 'Error al conseguir la entrada gratuita');
        setLoading(false);
      }
      return;
    }

    try {
      const res = await createPaymentSession({
        event_id: id,
        quantity,
        attendees,
        ticket_type_id: ticketType?.id || null,
        ticket_type_name: ticketType?.name || null,
        price: ticketType?.price || null,
        bus_ida: busIda || null,
        bus_vuelta: busVuelta || null,
      });
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Error al procesar el pago');
      setLoading(false);
    }
  };

  if (!event) return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <p style={{ color: colors.textSecondary, padding: '2rem' }}>Cargando...</p>
    </div>
  );

  const price = ticketType ? parseFloat(ticketType.price) : parseFloat(event.price);
  const fee = ticketType?.custom_fee ? parseFloat(ticketType.custom_fee) : parseFloat(calcFee(price));
  const total = (price * quantity) + (fee * quantity);

  const selectStyle = {
    width: '100%',
    backgroundColor: colors.inputBg,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    color: colors.text,
    fontSize: '0.9rem',
    outline: 'none',
    cursor: 'pointer',
  };

  const horariosIda = busSchedules.filter(s => s.type === 'ida');
  const horariosVuelta = busSchedules.filter(s => s.type === 'vuelta');
  const formatTime = (t) => (t || '').slice(0, 5);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Navbar />
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1100px', margin: '0 auto' }}>

        <button style={{ backgroundColor: 'transparent', border: 'none', color: colors.textSecondary, padding: '0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '1.5rem' }} onClick={() => navigate(`/${id}`)}>
          <ArrowBackIcon style={{ fontSize: 16 }} /> Volver
        </button>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ backgroundColor: colors.bgSecondary, borderRadius: '16px', border: `1px solid ${colors.border}`, padding: isMobile ? '1.2rem' : '2.5rem', width: '100%', maxWidth: '700px' }}>

            <h1 style={{ color: colors.text, fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 'bold', marginBottom: '1.2rem' }}>Comprar entradas</h1>

            {(event.banner_image || event.image) && (
              <img
                src={event.banner_image || event.image}
                alt={event.name}
                style={{
                  width: '100%',
                  height: isMobile ? '160px' : '220px',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  borderRadius: '12px',
                  marginBottom: '1.2rem'
                }}
              />
            )}

            <p style={{ color: colors.accent, fontSize: '0.72rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>{event.category || 'Evento'}</p>
            <h2 style={{ color: colors.text, fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 'bold', marginBottom: '0.4rem' }}>{event.name}</h2>
            <p style={{ color: colors.textSecondary, fontSize: '0.82rem', marginBottom: '1.2rem' }}>
              {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} · {event.location}
            </p>

            {ticketType && (
              <div style={{ backgroundColor: colors.bgTertiary, borderRadius: '12px', padding: '1rem', marginBottom: '1.2rem', border: `1px solid ${colors.accent}` }}>
                <p style={{ color: colors.textSecondary, fontSize: '0.75rem', margin: '0 0 0.3rem' }}>Tipo de entrada seleccionado</p>
                <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.95rem', margin: 0 }}>{ticketType.name}</p>
                {ticketType.description && (
                  <p style={{ color: colors.textSecondary, fontSize: '0.82rem', margin: '0.3rem 0 0' }}>{ticketType.description}</p>
                )}
              </div>
            )}

            {/* Selector horario autobús */}
            {(ticketType?.includes_bus === true || ticketType?.includes_bus === 'true') && (
              <div style={{ backgroundColor: colors.bgTertiary, borderRadius: '12px', padding: '1rem', marginBottom: '1.2rem', border: '1px solid #1a3a5c' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <DirectionsBusIcon style={{ color: '#55bbff', fontSize: 20 }} />
                  <span style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.95rem' }}>Horario del autobús</span>
                </div>
                <p style={{ color: colors.textSecondary, fontSize: '0.8rem', marginBottom: '1rem' }}>
                  Salida desde c/ Arenal (Vigo). 55 plazas por trayecto.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Horario de ida *</label>
                    <select style={selectStyle} value={busIda} onChange={e => setBusIda(e.target.value)}>
                      <option value="">Selecciona horario</option>
                      {horariosIda.map(s => (
                        <option key={s.id} value={formatTime(s.time)} disabled={s.sold >= s.capacity}>
                          {formatTime(s.time)}h — c/ Arenal (Vigo){s.sold >= s.capacity ? ' (COMPLETO)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Horario de vuelta *</label>
                    <select style={selectStyle} value={busVuelta} onChange={e => setBusVuelta(e.target.value)}>
                      <option value="">Selecciona horario</option>
                      {horariosVuelta.map(s => (
                        <option key={s.id} value={formatTime(s.time)} disabled={s.sold >= s.capacity}>
                          {formatTime(s.time)}h — c/ Arenal (Vigo){s.sold >= s.capacity ? ' (COMPLETO)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* CANTIDAD */}
            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1.2rem', marginBottom: '1.2rem' }}>
              <label style={{ color: colors.text, fontWeight: 'bold', display: 'block', marginBottom: '0.8rem', fontSize: '0.9rem' }}>Cantidad de entradas:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button style={{ backgroundColor: colors.accent, color: colors.accentText, border: 'none', width: '34px', height: '34px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.2rem', fontWeight: 'bold' }} onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
                <span style={{ color: colors.text, fontSize: '1.3rem', fontWeight: 'bold', minWidth: '30px', textAlign: 'center' }}>{quantity}</span>
                <button style={{ backgroundColor: colors.accent, color: colors.accentText, border: 'none', width: '34px', height: '34px', borderRadius: '8px', cursor: 'pointer', fontSize: '1.2rem', fontWeight: 'bold' }} onClick={() => setQuantity(q => Math.min(8, q + 1))}>+</button>
              </div>
              <p style={{ color: colors.textSecondary, fontSize: '0.75rem', marginTop: '0.4rem' }}>Máximo 8 entradas por usuario</p>
            </div>

            {/* DATOS ASISTENTES */}
            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '1.2rem', marginBottom: '1.2rem' }}>
              <label style={{ color: colors.text, fontWeight: 'bold', display: 'block', marginBottom: '0.8rem', fontSize: '0.9rem' }}>Datos de los asistentes:</label>
              {attendees.map((attendee, index) => (
                <div key={index} style={{ backgroundColor: colors.bgTertiary, borderRadius: '12px', padding: '1rem', marginBottom: '0.8rem', border: `1px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem' }}>
                    <PersonIcon style={{ color: colors.accent, fontSize: 16 }} />
                    <span style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.85rem' }}>Entrada {index + 1}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
                    {[
                      { label: 'Nombre completo', field: 'name', type: 'text', placeholder: 'Nombre del asistente' },
                      { label: 'Email', field: 'email', type: 'email', placeholder: 'email@ejemplo.com' },
                      { label: 'DNI / NIE', field: 'dni', type: 'text', placeholder: '12345678A' },
                      { label: 'Fecha de nacimiento', field: 'birthdate', type: 'date', placeholder: '' },
                    ].map(f => (
                      <div key={f.field}>
                        <label style={{ color: colors.textSecondary, fontSize: '0.75rem', display: 'block', marginBottom: '0.2rem' }}>{f.label}</label>
                        <input
                          style={{ width: '100%', backgroundColor: colors.inputBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '0.55rem 0.8rem', color: colors.text, fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
                          type={f.type}
                          placeholder={f.placeholder}
                          value={attendee[f.field]}
                          onChange={(e) => handleAttendeeChange(index, f.field, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* RESUMEN */}
            <div style={{ backgroundColor: colors.bgTertiary, borderRadius: '12px', padding: '1rem', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>Precio por entrada</span>
                <span style={{ color: colors.text, fontSize: '0.85rem' }}>{ticketType?.is_free ? 'GRATIS' : `${price}€`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={{ color: colors.textSecondary, fontSize: '0.85rem' }}>Gasto de gestión <FeeInfoButton /></span>
                <span style={{ color: '#ffaa00', fontSize: '0.85rem' }}>{ticketType?.is_free ? '0.00€' : `${fee}€ x ${quantity}`}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${colors.border}`, paddingTop: '0.6rem', marginTop: '0.6rem' }}>
                <span style={{ color: colors.text, fontWeight: 'bold' }}>Total (IVA incluido)</span>
                <span style={{ color: colors.accent, fontWeight: 'bold', fontSize: '1.2rem' }}>{ticketType?.is_free ? 'GRATIS' : `${total.toFixed(2)}€`}</span>
              </div>
            </div>

            {error && (
              <div style={{ backgroundColor: '#ff444422', border: '1px solid #ff4444', color: '#ff4444', padding: '0.7rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' }}>
                {error}
              </div>
            )}

            <button
              type="button"
              style={{ backgroundColor: loading ? colors.bgTertiary : colors.accent, color: loading ? colors.textSecondary : colors.accentText, border: 'none', padding: '0.9rem', borderRadius: isMobile ? '4px' : '25px', cursor: loading ? 'default' : 'pointer', fontWeight: 'bold', fontSize: isMobile ? '0.9rem' : '1rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', textTransform: isMobile ? 'uppercase' : 'none', letterSpacing: isMobile ? '0.05em' : 'normal' }}
              onClick={handleBuy}
              disabled={loading}
            >
              <LockIcon style={{ fontSize: 16 }} />
              {ticketType?.is_free
                ? (loading ? 'Procesando...' : 'Conseguir entrada gratis')
                : (loading ? 'Redirigiendo a Stripe...' : `Pagar · ${total.toFixed(2)}€`)}
            </button>
            <p style={{ color: colors.textSecondary, fontSize: '0.75rem', textAlign: 'center', marginTop: '0.6rem' }}>
              🔒 Pago seguro procesado por Stripe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;